
import { useState } from 'react';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  url: string;
  description?: string;
  image?: string;
}

const ShareModal = ({ isOpen, onClose, title, url, description, image }: ShareModalProps) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const shareUrl = encodeURIComponent(url);
  const shareTitle = encodeURIComponent(title);
  const shareDescription = encodeURIComponent(description || '');

  const shareOptions = [
    {
      name: 'WhatsApp',
      icon: 'ri-whatsapp-fill',
      color: 'bg-green-500',
      url: `https://wa.me/?text=${shareTitle}%20${shareUrl}`
    },
    {
      name: 'Facebook',
      icon: 'ri-facebook-fill',
      color: 'bg-blue-600',
      url: `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`
    },
    {
      name: 'Twitter',
      icon: 'ri-twitter-x-fill',
      color: 'bg-gray-900',
      url: `https://twitter.com/intent/tweet?text=${shareTitle}&url=${shareUrl}`
    },
    {
      name: 'LinkedIn',
      icon: 'ri-linkedin-fill',
      color: 'bg-blue-700',
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`
    },
    {
      name: 'Email',
      icon: 'ri-mail-fill',
      color: 'bg-gray-600',
      url: `mailto:?subject=${shareTitle}&body=${shareDescription}%0A%0A${shareUrl}`
    },
    {
      name: 'Telegram',
      icon: 'ri-telegram-fill',
      color: 'bg-sky-500',
      url: `https://t.me/share/url?url=${shareUrl}&text=${shareTitle}`
    }
  ];

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleShare = (shareUrl: string) => {
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Share this deal</h3>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl text-gray-500"></i>
            </button>
          </div>
        </div>

        {/* Preview */}
        {(title || image) && (
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-100">
            <div className="flex items-center gap-4">
              {image && (
                <img src={image} alt="" className="w-16 h-16 object-contain rounded-lg bg-white" />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">{title}</p>
                {description && (
                  <p className="text-sm text-gray-500 truncate">{description}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Share Options */}
        <div className="p-6">
          <div className="grid grid-cols-3 gap-4 mb-6">
            {shareOptions.map((option) => (
              <button
                key={option.name}
                onClick={() => handleShare(option.url)}
                className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer group"
              >
                <div className={`w-12 h-12 ${option.color} rounded-full flex items-center justify-center group-hover:scale-110 transition-transform`}>
                  <i className={`${option.icon} text-white text-xl`}></i>
                </div>
                <span className="text-xs text-gray-600">{option.name}</span>
              </button>
            ))}
          </div>

          {/* Copy Link */}
          <div className="flex items-center gap-2 p-3 bg-gray-100 rounded-xl">
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-600 truncate">{url}</p>
            </div>
            <button
              onClick={handleCopyLink}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all cursor-pointer whitespace-nowrap ${
                copied 
                  ? 'bg-green-500 text-white' 
                  : 'bg-gray-900 text-white hover:bg-gray-800'
              }`}
            >
              {copied ? (
                <span className="flex items-center gap-1">
                  <i className="ri-check-line"></i>
                  Copied!
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <i className="ri-file-copy-line"></i>
                  Copy
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
